import {
  BookTextIcon,
  CalendarCheckIcon,
  CalendarDaysIcon,
  CirclePlayIcon,
  HistoryIcon,
  TextAlignStartIcon,
  TriangleAlertIcon,
  ZapIcon,
} from "lucide-react";

import { EnumColors } from "@/constants/values";
import { ObjectType } from "@/objects/@types";

type ExecutionFragment = {
  id: string;
  identifier: string;
  createdAt: string;
  updatedAt: string;
  source: string;
  status: string;
  triggerType?: string | null;
  startedAt?: string | null;
  completedAt?: string | null;
  error?: string | null;
  seedContext?: unknown;
  rule?: {
    id: string;
    identifier: string;
    name?: string | null;
  } | null;
  thread?: {
    id: string;
    identifier: string;
    name?: string | null;
    entity?: {
      id: string;
      identifier: string;
      name?: string | null;
    } | null;
  } | null;
};

const STATUS_OPTIONS = [
  { key: "queued", value: "Queued" },
  { key: "running", value: "Running" },
  { key: "succeeded", value: "Succeeded" },
  { key: "failed", value: "Failed" },
  { key: "skipped", value: "Skipped" },
  { key: "pending_confirmation", value: "Pending Confirmation" },
];

const TRIGGER_TYPE_OPTIONS = [
  { key: "manual", value: "Manual" },
  { key: "event", value: "Event" },
  { key: "schedule", value: "Schedule" },
];

const SOURCE_OPTIONS = [
  { key: "rule", value: "Rule" },
  { key: "agent", value: "Agent" },
];

const object: ObjectType<ExecutionFragment> = {
  humanName: "execution",
  inputKey: "execution",
  typename: "ExecutionObject",
  ReactIcon: HistoryIcon,
  titleFieldKey: "identifier",
  getTitle: (data) =>
    data?.rule?.name ?? data?.thread?.name ?? data?.identifier ?? "",
  getEntityId: (data) => data?.thread?.entity?.id ?? null,
  isEntity: false,
  fieldDefinitions: [
    {
      objectKey: "status",
      label: "Status",
      type: "enum",
      ReactIcon: CirclePlayIcon,
      asString: (data) => data?.status ?? "",
      getValue: (data) => data?.status ?? "",
      getValueOptions: () => ({
        enum: STATUS_OPTIONS,
        getColors: (mode) => ({
          queued: EnumColors.GREY_1[mode],
          running: EnumColors.BLUE[mode],
          succeeded: EnumColors.GREEN[mode],
          failed: EnumColors.RED[mode],
          skipped: EnumColors.ORANGE[mode],
          pending_confirmation: EnumColors.ORANGE[mode],
        }),
      }),
      updateProps: { isEditable: false, isVisible: (data) => !!data?.status },
      summaryProps: { isVisible: (data) => !!data?.status },
      fieldProps: {
        headerFieldType: "enum",
        isSortable: true,
        isFilterable: true,
      },
    },
    {
      objectKey: "triggerType",
      label: "Trigger",
      type: "enum",
      ReactIcon: ZapIcon,
      asString: (data) => data?.triggerType ?? "",
      getValue: (data) => data?.triggerType ?? "",
      getValueOptions: () => ({
        enum: TRIGGER_TYPE_OPTIONS,
        getColors: (mode) => ({
          manual: EnumColors.GREY_1[mode],
          event: EnumColors.PURPLE[mode],
          schedule: EnumColors.BLUE[mode],
        }),
      }),
      updateProps: {
        isEditable: false,
        isVisible: (data) => !!data?.triggerType,
      },
      summaryProps: { isVisible: (data) => !!data?.triggerType },
      fieldProps: {
        headerFieldType: "enum",
        isSortable: true,
        isFilterable: true,
      },
    },
    {
      objectKey: "rule",
      label: "Rule",
      type: "reference",
      ReactIcon: BookTextIcon,
      asString: (data) => data?.rule?.name ?? data?.rule?.identifier ?? "",
      getValue: (data) =>
        data?.rule
          ? {
              __typename: "RuleDefinitionObject",
              id: data.rule.id,
              identifier: data.rule.identifier,
              name: data.rule.name ?? "",
            }
          : (null as any),
      updateProps: {
        isEditable: false,
        isVisible: (data) => !!data?.rule,
      },
      summaryProps: { isVisible: (data) => !!data?.rule },
      fieldProps: {
        headerFieldType: "reference",
        isSortable: false,
        isFilterable: false,
      },
    },
    {
      objectKey: "identifier",
      label: "Identifier",
      type: "text",
      ReactIcon: TextAlignStartIcon,
      asString: (data) => data?.identifier ?? "",
      getValue: (data) => data?.identifier ?? "",
      updateProps: { isEditable: false, isVisible: () => true },
      summaryProps: { isVisible: () => true },
      fieldProps: {
        headerFieldType: "string",
        isSortable: false,
        isFilterable: false,
      },
    },
    {
      objectKey: "startedAt",
      label: "Started",
      type: "date",
      ReactIcon: CalendarDaysIcon,
      asString: (data) => data?.startedAt ?? "",
      getValue: (data) => data?.startedAt ?? "",
      updateProps: {
        isEditable: false,
        isVisible: (data) => !!data?.startedAt,
      },
      summaryProps: { isVisible: (data) => !!data?.startedAt },
      fieldProps: {
        headerFieldType: "date",
        isSortable: true,
        isFilterable: true,
      },
    },
    {
      objectKey: "completedAt",
      label: "Completed",
      type: "date",
      ReactIcon: CalendarCheckIcon,
      asString: (data) => data?.completedAt ?? "",
      getValue: (data) => data?.completedAt ?? "",
      updateProps: {
        isEditable: false,
        isVisible: (data) => !!data?.completedAt,
      },
      summaryProps: { isVisible: (data) => !!data?.completedAt },
      fieldProps: {
        headerFieldType: "date",
        isSortable: true,
        isFilterable: true,
      },
    },
    {
      objectKey: "error",
      label: "Error",
      type: "text",
      ReactIcon: TriangleAlertIcon,
      asString: (data) => data?.error ?? "",
      getValue: (data) => data?.error ?? "",
      updateProps: { isEditable: false, isVisible: (data) => !!data?.error },
      summaryProps: { isVisible: (data) => !!data?.error },
      fieldProps: {
        headerFieldType: "string",
        isSortable: false,
        isFilterable: true,
      },
    },
    {
      objectKey: "source",
      label: "Source",
      type: "enum",
      ReactIcon: ZapIcon,
      asString: (data) => data?.source ?? "",
      getValue: (data) => data?.source ?? "",
      getValueOptions: () => ({
        enum: SOURCE_OPTIONS,
        getColors: (mode) => ({
          rule: EnumColors.PURPLE[mode],
          agent: EnumColors.BLUE[mode],
        }),
      }),
      updateProps: { isEditable: false, isVisible: (data) => !!data?.source },
      summaryProps: { isVisible: (data) => !!data?.source },
    },
  ],
};

export { object as ExecutionObject };
